import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import {
  Wallet, TrendingUp, TrendingDown, Banknote, RefreshCw,
  Plus, Trash2, Receipt, Loader2
} from 'lucide-react';

const SYMBOL = { GBP: '£', INR: '₹', USD: '$', CAD: 'C$', EUR: '€' };
const fmt = (amt, ccy) => `${SYMBOL[ccy] || ccy + ' '}${Number(amt).toFixed(2)}`;

const StatCard = ({ label, value, sub, accent = 'text-[#E07A3A]', icon }) => (
  <div className="bg-white border border-slate-200 px-5 py-4">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs tracking-[0.2em] uppercase text-slate-500">{label}</span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export const AdminStripeBalance = () => {
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [history, setHistory] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vendor: 'Emergent', category: 'ai-credits',
    amount: '', currency: 'GBP', description: '', invoice_url: '',
  });

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [b, p, h, e] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stripe/balance`),
        axios.get(`${API_URL}/api/admin/stripe/payouts?limit=10`),
        axios.get(`${API_URL}/api/admin/stripe/balance-history?limit=20`),
        axios.get(`${API_URL}/api/admin/stripe/expenses?limit=50`),
      ]);
      setBalance(b.data);
      setPayouts(p.data.items || []);
      setHistory(h.data.items || []);
      setExpenses(e.data.items || []);
      setTotals(e.data.totals_by_vendor || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Stripe data');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitExpense = async (e) => {
    e.preventDefault();
    if (!form.vendor || !form.amount) {
      toast.error('Vendor and amount required'); return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/stripe/expenses`, {
        ...form, amount: parseFloat(form.amount),
      });
      toast.success(`Logged ${form.vendor} expense`);
      setShowForm(false);
      setForm({ vendor: 'Emergent', category: 'ai-credits',
        amount: '', currency: 'GBP', description: '', invoice_url: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const removeExpense = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/stripe/expenses/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const totalAvailable = (balance?.available || []).reduce((acc, b) =>
    acc + (b.currency === 'GBP' ? b.amount : 0), 0);
  const totalPending = (balance?.pending || []).reduce((acc, b) =>
    acc + (b.currency === 'GBP' ? b.amount : 0), 0);
  const totalSpentGBP = Object.values(totals).reduce((a, t) => a + (t.GBP || 0), 0);
  const netGBP = totalAvailable - totalSpentGBP;

  if (loading) {
    return <div className="p-8 text-center text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading Stripe…
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-stripe-balance">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs tracking-[0.3em] uppercase text-[#E07A3A] block mb-1 font-medium">Stripe Treasury</span>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Live Balance & Business Spend</h2>
          <p className="text-sm text-slate-500 mt-1">
            {balance?.livemode === false ? '🟡 Test mode' : '🟢 Live mode'} ·
            Last refreshed {balance?.fetched_at ? new Date(balance.fetched_at).toLocaleTimeString() : '—'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchAll} disabled={refreshing}
            className="px-4 py-2.5 text-sm tracking-wider uppercase font-semibold border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white disabled:opacity-50 flex items-center gap-2"
            data-testid="btn-refresh-stripe">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button onClick={() => setShowForm(s => !s)}
            className="px-4 py-2.5 text-sm tracking-wider uppercase font-semibold bg-[#E07A3A] text-white hover:bg-[#C4652E] flex items-center gap-2"
            data-testid="btn-log-expense">
            <Plus className="h-4 w-4" /> Log Expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Available (GBP)"
          value={fmt(totalAvailable, 'GBP')}
          sub={(balance?.available || []).filter(b => b.currency !== 'GBP')
            .map(b => `+ ${fmt(b.amount, b.currency)}`).join(', ') || 'GBP only'}
          icon={<Wallet className="h-4 w-4" />} />
        <StatCard
          label="Pending"
          value={fmt(totalPending, 'GBP')}
          sub="Settles in 2-7 days"
          icon={<Banknote className="h-4 w-4" />}
          accent="text-amber-600" />
        <StatCard
          label="Business Spend (GBP)"
          value={fmt(totalSpentGBP, 'GBP')}
          sub={`${expenses.length} logged expenses`}
          icon={<TrendingDown className="h-4 w-4" />}
          accent="text-rose-600" />
        <StatCard
          label="Net Position"
          value={fmt(netGBP, 'GBP')}
          sub="Available − spent"
          icon={<TrendingUp className="h-4 w-4" />}
          accent={netGBP >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
      </div>

      {showForm && (
        <form onSubmit={submitExpense} className="bg-amber-50 border border-amber-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <select value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})}
            className="border border-slate-300 px-3 py-2 text-sm" data-testid="exp-vendor">
            <option>Emergent</option><option>Firecrawl</option><option>Resend</option>
            <option>Twilio</option><option>Meta</option><option>Apify</option>
            <option>Cloudflare</option><option>Other</option>
          </select>
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
            className="border border-slate-300 px-3 py-2 text-sm">
            <option value="ai-credits">AI / dev credits</option>
            <option value="scraping">Scraping</option>
            <option value="email">Email</option>
            <option value="sms">SMS / WhatsApp</option>
            <option value="hosting">Hosting</option>
            <option value="domain">Domain / DNS</option>
            <option value="other">Other</option>
          </select>
          <div className="flex gap-2">
            <input type="number" step="0.01" placeholder="Amount" value={form.amount}
              onChange={e => setForm({...form, amount: e.target.value})}
              className="border border-slate-300 px-3 py-2 text-sm flex-1" data-testid="exp-amount" required />
            <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
              className="border border-slate-300 px-3 py-2 text-sm">
              <option>GBP</option><option>USD</option><option>EUR</option><option>INR</option>
            </select>
          </div>
          <input placeholder="Description (optional)" value={form.description}
            onChange={e => setForm({...form, description: e.target.value})}
            className="border border-slate-300 px-3 py-2 text-sm md:col-span-2" />
          <input placeholder="Invoice URL (optional)" value={form.invoice_url}
            onChange={e => setForm({...form, invoice_url: e.target.value})}
            className="border border-slate-300 px-3 py-2 text-sm" />
          <div className="md:col-span-3 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm border border-slate-300">Cancel</button>
            <button type="submit"
              className="px-4 py-2 text-sm bg-[#E07A3A] text-white" data-testid="btn-save-expense">Save</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white border border-slate-200">
          <header className="px-5 py-3 border-b border-slate-100">
            <span className="font-semibold text-sm tracking-wide flex items-center gap-2">
              <Banknote className="h-4 w-4 text-[#E07A3A]" /> Recent Payouts to Bank
            </span>
          </header>
          <div className="divide-y divide-slate-100">
            {payouts.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-sm">No payouts yet.</div>
            )}
            {payouts.map(p => (
              <div key={p.id} className="px-5 py-3 flex justify-between items-center">
                <div>
                  <div className="font-medium text-sm">{fmt(p.amount, p.currency)}</div>
                  <div className="text-xs text-slate-500">
                    {p.status} · {p.method} · arrives {p.arrival_date ? new Date(p.arrival_date).toLocaleDateString() : '—'}
                  </div>
                </div>
                <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 ${
                  p.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                  p.status === 'pending' ? 'bg-amber-950/95 text-amber-300 border border-amber-500/50 rounded' : 'bg-slate-900 text-slate-300 border border-slate-500/40 rounded'
                }`}>{p.status}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200">
          <header className="px-5 py-3 border-b border-slate-100">
            <span className="font-semibold text-sm tracking-wide flex items-center gap-2">
              <Receipt className="h-4 w-4 text-[#E07A3A]" /> Balance Movements
            </span>
          </header>
          <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
            {history.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-sm">No activity yet.</div>
            )}
            {history.map(t => (
              <div key={t.id} className="px-5 py-2.5 flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{t.type}</div>
                  <div className="text-xs text-slate-400 truncate">{t.description || '—'}</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className={`text-sm font-medium ${t.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {t.net >= 0 ? '+' : ''}{fmt(t.net, t.currency)}
                  </div>
                  {t.fee > 0 && <div className="text-[10px] text-slate-400">fee {fmt(t.fee, t.currency)}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="bg-white border border-slate-200">
        <header className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
          <span className="font-semibold text-sm tracking-wide flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-rose-600" /> Logged Business Expenses
          </span>
          <span className="text-xs text-slate-400">{expenses.length} entries</span>
        </header>
        <div className="divide-y divide-slate-100">
          {expenses.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">
              No expenses logged yet. Click <strong>Log Expense</strong> above to track Emergent / Firecrawl / Resend top-ups.
            </div>
          )}
          {expenses.map(e => (
            <div key={e.id} className="px-5 py-3 flex justify-between items-center">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">
                  {e.vendor} <span className="text-xs text-slate-400 font-normal">· {e.category}</span>
                </div>
                <div className="text-xs text-slate-500">{e.description || '—'}</div>
                {e.invoice_url && (
                  <a href={e.invoice_url} target="_blank" rel="noreferrer"
                    className="text-xs text-[#E07A3A] hover:underline">View invoice ↗</a>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-rose-700">−{fmt(e.amount, e.currency)}</div>
                <div className="text-xs text-slate-400">
                  {e.paid_at ? new Date(e.paid_at).toLocaleDateString() : '—'}
                </div>
              </div>
              <button onClick={() => removeExpense(e.id)}
                className="ml-3 text-slate-400 hover:text-rose-600 transition-colors" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {Object.keys(totals).length > 0 && (
          <div className="border-t border-slate-100 px-5 py-3 bg-slate-50">
            <div className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">Totals by vendor</div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(totals).map(([v, byCcy]) => (
                <div key={v} className="text-xs bg-white border border-slate-200 px-2 py-1">
                  <strong>{v}</strong>: {Object.entries(byCcy).map(([c, a]) =>
                    fmt(a, c)).join(' + ')}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
